import type { SpaceEntry } from './types.js'
import { cloneSpaceEntry, isDeletionReceipt } from './lib/spaceCodec.js'

// Snapshot-based history with relaxed, coalesced text checkpoints.
export class History {
  private undoStack: SpaceEntry[][] = []
  private redoStack: SpaceEntry[][] = []
  private readonly maxDepth: number

  constructor(opts?: { maxDepth?: number }) {
    this.maxDepth = opts?.maxDepth ?? 64
  }

  // Record a snapshot before a non-text mutation.
  push(snapshot: SpaceEntry[]): boolean {
    const clone = this.cloneBlocks(snapshot)
    const last = this.undoStack[this.undoStack.length - 1]
    if (last && this.equalSnapshots(last, clone)) {
      this.redoStack = []
      return false
    }
    this.undoStack.push(clone)
    this.trim(this.undoStack)
    this.redoStack = []
    return true
  }

  undo(currentSnapshot: SpaceEntry[]): SpaceEntry[] | undefined {
    const prev = this.undoStack.pop()
    if (!prev) return undefined
    this.redoStack.push(this.cloneBlocks(currentSnapshot))
    this.trim(this.redoStack)
    return this.cloneBlocks(prev)
  }

  redo(currentSnapshot: SpaceEntry[]): SpaceEntry[] | undefined {
    const next = this.redoStack.pop()
    if (!next) return undefined
    this.undoStack.push(this.cloneBlocks(currentSnapshot))
    this.trim(this.undoStack)
    return this.cloneBlocks(next)
  }

  canUndo(): boolean {
    return this.undoStack.length > 0
  }

  canRedo(): boolean {
    return this.redoStack.length > 0
  }

  private trim(stack: SpaceEntry[][]): void {
    while (stack.length > this.maxDepth) {
      stack.shift()
    }
  }

  private cloneBlocks(blocks: SpaceEntry[]): SpaceEntry[] {
    return blocks.map(cloneSpaceEntry)
  }

  private equalSnapshots(a: SpaceEntry[], b: SpaceEntry[]): boolean {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      const pa = a[i]
      const pb = b[i]
      if (!pb) return false
      if (pa.text !== pb.text) return false
      if (pa.ided[0] !== pb.ided[0] || pa.ided[1] !== pb.ided[1]) return false
      if (isDeletionReceipt(pa) || isDeletionReceipt(pb)) {
        if (!isDeletionReceipt(pa) || !isDeletionReceipt(pb)) return false
        continue
      }
      if (!this.equalMeta(pa.meta, pb.meta)) return false
      const [ax, ay, az, aw] = pa.xyzw
      const [bx, by, bz, bw] = pb.xyzw
      if (ax !== bx || ay !== by || az !== bz || aw !== bw) return false
      if (pa.link.length !== pb.link.length) return false
      for (let j = 0; j < pa.link.length; j++) {
        if (pa.link[j] !== pb.link[j]) return false
      }
    }
    return true
  }

  private equalMeta(a?: Record<string, number>, b?: Record<string, number>): boolean {
    const aKeys = a ? Object.keys(a) : []
    const bKeys = b ? Object.keys(b) : []
    if (aKeys.length !== bKeys.length) return false
    for (const key of aKeys) {
      if ((a ? a[key] : undefined) !== (b ? b[key] : undefined)) return false
    }
    return true
  }
}
